import { ReturnGroupDto } from '@cratehackers/api-client';
import {
  IonAlert,
  IonButton,
  IonCard,
  IonCardContent,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonPopover,
  IonProgressBar,
  IonSpinner,
  IonText,
  IonTextarea,
  IonToggle,
} from '@ionic/react';
import sha256 from 'crypto-js/sha256';
import {
  add,
  arrowBack,
  arrowUp,
  briefcase,
  chevronDown,
  colorWand,
  heartOutline,
  pricetag,
  saveOutline,
  settings,
  trash,
} from 'ionicons/icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { config } from '../../../config';
import { on, removeListener, run } from '../../../helper/electron';
import { useHasBusinessPlan } from '../../../hooks/useHasBusinessPlan';
import { useIcons } from '../../../hooks/useIcons';
import ApiService from '../../../services/Api.service';
import { CrateService } from '../../../services/Crate.service';
import { DescriptionGenerationService } from '../../../services/DescriptionGeneration.service';
import PostHogService from '../../../services/PostHog.service';
import RequestService from '../../../services/Request.service';
import {
  addTrackToCrate,
  fetchCrateById,
  incrementCrateMetric,
  removeTrackFromCrate,
  saveCrateChanges,
  saveCrateChangesNow,
  selectCrateState,
  selectFilteredCrateTracks,
  selectIsCrateSaving,
  setCrateTracks,
  setLocationFrom,
  setMatchFilter,
  setSelectedCrate,
  setSelectedCrateDescription,
  setSelectedCrateGroupId,
  setSelectedCrateName,
  setSelectedCrateStatus,
  setSelectedCrateStreamingLinks,
  setSelectedCrateVideoEmbed,
  uploadCrateImage,
} from '../../../store/slices/crate.slice';
import { seedCrateMetrics } from '../../../store/slices/crateMetrics.slice';
import {
  bulkLoadAndSelect,
  clearTrackMatches,
  getAllSavedFilePaths,
  removeTrackFromState,
  selectAllTrackMatches,
  selectBulkProgress,
  selectSelectedMatches,
  selectShowMatcher,
  setShowMatcher,
} from '../../../store/slices/track-matcher.slice';
import { fetchTrackById } from '../../../store/slices/track.slice';
import { AppDispatch } from '../../../store/store';
import { iCrate } from '../../../types/ICrate';
import { iHacker } from '../../../types/IHackers';
import { iTrack } from '../../../types/ITrack';
import { cleanArtist, cleanTitle } from '../../../utils/cleanTrackMetadata';
import { ensureTrackIds } from '../../../utils/ensureTrackId';
import { generateTrackID } from '../../../utils/generateTrackID';
import isElectron from '../../../utils/isElectron';
import { openExternalLink } from '../../../utils/openExternalLink';
import { CategoriesEditable } from '../../categories/CategoriesEditable';
import { HackersInline } from '../../hackers/Hackers';
import { useLoading } from '../../RootLoader/useLoading';
import {
  SaveStatus,
  SaveStatusIndicator,
} from '../../SaveStatusIndicator/SaveStatusIndicator';
import { SavingSpinner } from '../../SavingSpinner/SavingSpinner';
import { useToast } from '../../ToastManager/useToast';
import { HiddenTracks } from '../../tracks/HiddenTracks/HiddenTracks';
import { Recommendations } from '../../tracks/Recommendations';
import SearchComponent from '../../tracks/SearchComponent';
import { Tracks } from '../../tracks/Tracks';
import { CrateFilter } from '../CrateFilter/CrateFilter';
import { CrateFilterReorderDialog } from '../CrateFilterReorderDialog/CrateFilterReorderDialog';
import '../Crates.scss';
import { CloudCrates } from './CloudCrates';
import { CrateMetrics } from './CrateMetrics';
import ImageCropper from './ImageCropper';
import { InlineExportButtons } from './InlineExportButtons';
import { StreamingSaveButtons } from './StreamingSaveButtons';
import { mergePlayCounts } from '../../../utils/mergePlayCounts';
import {
  markTrackMatcherBoundToCrateId,
  shouldClearTrackMatchesForCrateId,
} from '../../../utils/shouldClearTrackMatchesForCrateId';

interface iCrateProps {
  id?: string;
  tracks?: iTrack[];
  withTracks?: boolean;
  sortingAvailable?: boolean;
}

interface ICloudCrateLink {
  name: string;
  link: string;
}

interface CrateLocationState {
  fromPage?: string;
}
function ensureAbsoluteUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('//')) return 'https:' + url;
  if (/^[\w.-]+\.[a-z]{2,}/i.test(url)) return `https://${url}`;
  return url;
}

export const Crate: React.FC<iCrateProps> = ({
  id,
  withTracks = false,
  tracks = [],
}) => {
  const { icons, loading } = useIcons();

  // ================= STATE ========================
  /* General Properties */
  const bulkProgress = useSelector(selectBulkProgress);
  const [hackers, setHackers] = useState<iHacker[]>([]);
  const [crateImageFile, setCrateImageFile] = useState<File | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [extractedColor, setExtractedColor] = useState<string | null>(null);

  // Hover-to-edit state for personal crates
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);

  // Unified actions menu
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Store original tracks for undo after cleanup
  const [preCleanupTracks, setPreCleanupTracks] = useState<iTrack[] | null>(
    null,
  );
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStreamingLinksModal, setShowStreamingLinksModal] = useState(false);

  const [editingVideo, setEditingVideo] = useState(false);

  /* General Properties */
  const [groups, setGroups] = useState<ReturnGroupDto[]>([]);
  const [groupsWithPermissions, setGroupsWithPermissions] = useState<any[]>([]);
  const [seratoPlayCounts, setSeratoPlayCounts] = useState<
    Record<string, { playCount: number }>
  >({});
  const [virtualDjPlayCounts, setVirtualDjPlayCounts] = useState<
    Record<string, { playCount: number }>
  >({});
  // Play counts load in the background via IPC; track their loading state so the
  // UI can show a note rather than acting on incomplete data.
  const [seratoPlayCountsLoading, setSeratoPlayCountsLoading] =
    useState<boolean>(isElectron());
  const [virtualDjPlayCountsLoading, setVirtualDjPlayCountsLoading] =
    useState<boolean>(isElectron());
  const [pendingMostPlayed, setPendingMostPlayed] = useState(false);
  const playCountsLoading =
    seratoPlayCountsLoading || virtualDjPlayCountsLoading;
  const [trackReorderInProgress, setTrackReorderInProgress] = useState<
    iTrack[] | null
  >(null);
  const hiddenTracksRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const hasIncrementedView = useRef(false);

  // Collapsed header state - shows compact sticky header when scrolled past main header
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [descriptionManuallyEdited, setDescriptionManuallyEdited] =
    useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [pendingTitleChange, setPendingTitleChange] = useState<string | null>(
    null,
  );

  const isDescriptionGenerationAvailable =
    DescriptionGenerationService.isConfigured();

  // ================= ROUTER ========================
  const matchExport = useRouteMatch('/crate/:id/export');
  const history = useHistory();
  const location = useLocation<CrateLocationState>();
  const { successToast, errorToast } = useToast();

  // REDUX
  const dispatch = useDispatch<AppDispatch>();
  const { setIsLoading } = useLoading();
  const {
    isEditable,
    actualIsEditable,
    previewMode,
    selectedCrate: crate,
    filter: { isFilterActive, matchFilter },
  } = useSelector(selectCrateState);
  const isSaving = useSelector(selectIsCrateSaving);
  const showTrackMatcher = useSelector(selectShowMatcher);
  const selectedMatchesMap = useSelector(selectSelectedMatches());
  const trackMatchesMap = useSelector(selectAllTrackMatches);
  const { visible: filteredTracks, hidden } = useSelector(
    selectFilteredCrateTracks,
  );
  const hasBusinessPlan = useHasBusinessPlan();

  // Auto-fit title font size to container width
  useEffect(() => {
    const fitTitle = () => {
      const titleEl = titleRef.current;
      if (!titleEl || !crate?.Title) return;

      const containerWidth = titleEl.parentElement?.clientWidth || 800;
      const maxFontSize = 56;
      const minFontSize = 20;

      // Reset to max size to measure
      titleEl.style.fontSize = `${maxFontSize}px`;

      // Reduce font size until text fits
      let fontSize = maxFontSize;
      while (titleEl.scrollWidth > containerWidth && fontSize > minFontSize) {
        fontSize -= 2;
        titleEl.style.fontSize = `${fontSize}px`;
      }
    };

    // Small delay to ensure DOM is rendered
    const timer = setTimeout(fitTitle, 50);

    // Refit on window resize
    window.addEventListener('resize', fitTitle);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', fitTitle);
    };
  }, [crate?.Title, isEditable]);

  useEffect(() => {
    if (isElectron() && crate?._id && crate?.Tracks?.length) {
      const trackIds = crate.Tracks.map((t) => t.ID || generateTrackID(t));
      dispatch(getAllSavedFilePaths({ crateId: crate._id, trackIds }));
    }
  }, [crate?._id, crate?.Tracks?.length, dispatch]);

  const { matchedCount, reviewCount, unmatchedCount } = useMemo(() => {
    if (!crate?.Tracks)
      return { matchedCount: 0, reviewCount: 0, unmatchedCount: 0 };

    let matched = 0;
    let review = 0;
    let unmatched = 0;

    crate.Tracks.forEach((track) => {
      const tid = track.ID || generateTrackID(track);
      const hasSelectedMatch = (selectedMatchesMap[tid]?.length ?? 0) > 0;
      const potentialMatches = trackMatchesMap[tid] || [];
      const maxScore = potentialMatches.reduce(
        (max, m) => Math.max(max, m.match_score ?? 0),
        0,
      );

      if (hasSelectedMatch || (potentialMatches.length > 0 && maxScore >= 80)) {
        matched++;
      } else if (potentialMatches.length > 0) {
        review++;
      } else {
        unmatched++;
      }
    });

    return {
      matchedCount: matched,
      reviewCount: review,
      unmatchedCount: unmatched,
    };
  }, [crate?.Tracks, selectedMatchesMap, trackMatchesMap]);

  const locationFrom = useMemo(() => {
    if (location.pathname.startsWith('/chart/')) {
      return 'charts';
    }
    return location.state?.fromPage || 'unknown';
  }, [location.pathname, location.state?.fromPage]);

  // Unified auto-save handler
  const handleAutoSave = useCallback(async () => {
    const activeElement = document.activeElement;
    if (
      activeElement?.tagName === 'ION-INPUT' ||
      activeElement?.getAttribute('contenteditable') === 'true'
    ) {
      return;
    }
    try {
      setSaveStatus('saving');
      await dispatch(saveCrateChanges());
      setSaveStatus('saved');
    } catch (error) {
      console.error('Auto-save error:', error);
      setSaveStatus('error');
    }
  }, [dispatch]);

  const handleTitleChange = async (e: any) => {
    const newTitle = e.target.value;
    await dispatch(setSelectedCrateName(newTitle));
    if (isDescriptionGenerationAvailable && newTitle.trim()) {
      if (descriptionManuallyEdited && crate?.Description) {
        setPendingTitleChange(newTitle);
        setShowRegenerateConfirm(true);
        return;
      }
      await generateDescriptionFromTitle(newTitle);
    }
    handleAutoSave();
  };
  const generateDescriptionFromTitle = async (title: string) => {
    if (!isDescriptionGenerationAvailable) {
      return;
    }
    setIsGeneratingDescription(true);
    try {
      const description =
        await DescriptionGenerationService.generateDescription({
          crateTitle: title.trim(),
          enforceSafety: true,
        });

      await dispatch(setSelectedCrateDescription(description));
      setDescriptionManuallyEdited(false);

      await dispatch(saveCrateChanges());
      setSaveStatus('saved');
    } catch (error) {
      console.error('Failed to generate description:', error);
      errorToast({
        message: 'Failed to generate description. You can edit it manually.',
      });
      setSaveStatus('error');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const confirmRegenerateDescription = async () => {
    if (pendingTitleChange) {
      await generateDescriptionFromTitle(pendingTitleChange);
      setPendingTitleChange(null);
    }
    setShowRegenerateConfirm(false);
    handleAutoSave();
  };

  const incrementCrateView = async () => {
    if (!id) return;
    try {
      dispatch(incrementCrateMetric({ crateId: id, metric: 'view' }));
    } catch (error) {
      console.log(`Error:${error}`);
    }
  };

  const handleDescriptionChange = async (description: string) => {
    setDescriptionManuallyEdited(true);
    setSaveStatus('idle');
    await dispatch(setSelectedCrateDescription(description));
    handleAutoSave();
  };

  const handleVideoChange = async (embedCode: string) => {
    await dispatch(setSelectedCrateVideoEmbed(embedCode));
  };

  const handleSaveVideoChange = async () => {
    try {
      setSaveStatus('saving');
      await dispatch(saveCrateChangesNow());
      setSaveStatus('saved');
      successToast({
        message: `Changed video for ${crate?.Title}`,
      });
      setEditingVideo(false);
    } catch (error) {
      setSaveStatus('error');
      errorToast({
        message: `Unable to change video for ${crate?.Title}`,
      });
    }
  };

  const getThumbnailFromEmbed = (embedHtml: string): string => {
    const match = embedHtml.match(/src="([^"]+)"/);
    if (!match) return '';
    const src = match[1];
    try {
      const url = new URL(src);

      // YouTube
      if (
        url.hostname.includes('youtube.com') ||
        url.hostname.includes('youtu.be')
      ) {
        const idMatch = src.match(/(?:embed\/|youtu\.be\/)([a-zA-Z0-9_-]+)/);
        if (idMatch) {
          return `https://img.youtube.com/vi/${idMatch[1]}/0.jpg`;
        }
      }

      // Vimeo
      if (url.hostname.includes('vimeo.com')) {
        const idMatch = src.match(/video\/(\d+)/);
        if (idMatch) {
          return `https://vumbnail.com/${idMatch[1]}.jpg`;
        }
      }

      // Kartra
      if (url.hostname.includes('kartra.com')) {
        return '/assets/icons/kartra-thumbnail.png';
      }

      // Dailymotion
      if (url.hostname.includes('dailymotion.com')) {
        let videoId = '';

        // standard embed path
        const idMatchPath = url.pathname.match(/video\/([^/?&]+)/);
        if (idMatchPath) videoId = idMatchPath[1];

        // geo.dailymotion.com/player.html?video=xxxxxx
        const idMatchParam = url.searchParams.get('video');
        if (idMatchParam) videoId = idMatchParam;

        return videoId
          ? `https://www.dailymotion.com/thumbnail/video/${videoId}`
          : '';
      }

      return '';
    } catch {
      return '';
    }
  };

  const getVideoUrlFromEmbed = (embedHtml: string): string => {
    const match = embedHtml.match(/src="([^"]+)"/);
    if (!match) return '';

    const srcUrl = match[1];

    try {
      const url = new URL(srcUrl);
      let videoId = '';

      // YouTube
      if (url.hostname.includes('youtube.com')) {
        if (url.pathname.startsWith('/embed/')) {
          videoId = url.pathname.split('/embed/')[1];
        } else {
          videoId = url.searchParams.get('v') || '';
        }
        return videoId ? `https://www.youtube.com/watch?v=${videoId}` : srcUrl;
      }

      if (url.hostname.includes('youtu.be')) {
        videoId = url.pathname.slice(1);
        return videoId ? `https://www.youtube.com/watch?v=${videoId}` : srcUrl;
      }

      // Vimeo
      if (url.hostname.includes('vimeo.com')) {
        const idMatch = url.pathname.match(/video\/(\d+)/);
        if (idMatch) {
          return `https://vimeo.com/${idMatch[1]}`;
        }
      }

      // Kartra
      if (url.hostname.includes('kartra.com')) {
        return srcUrl;
      }

      // Dailymotion
      if (url.hostname.includes('dailymotion.com')) {
        const idMatch = url.pathname.match(/video\/([^/?&]+)/);
        return idMatch
          ? `https://www.dailymotion.com/video/${idMatch[1]}`
          : srcUrl;
      }

      return srcUrl;
    } catch (error) {
      console.error('Invalid embed URL:', error);
      return '';
    }
  };

  const handleImageChange = async (file: File | null) => {
    try {
      setSaveStatus('saving');
      setCrateImageFile(file);
      if (file) {
        await dispatch(uploadCrateImage(file));
        await dispatch(saveCrateChanges());
        setSaveStatus('saved');
      } else {
        errorToast({ message: `No image file was added` });
        setSaveStatus('idle');
      }
    } catch (error) {
      console.error('Image change error:', error);
      setSaveStatus('error');
    }
  };

  const handleStreamingLinkUpdate = async (streamingLinks: Partial<iCrate>) => {
    try {
      setSaveStatus('saving');
      await dispatch(setSelectedCrateStreamingLinks(streamingLinks));
      await dispatch(saveCrateChangesNow());
      setSaveStatus('saved');
    } catch (error) {
      console.error('Streaming link update error:', error);
      setSaveStatus('error');
    }
  };

  const handleGroupIdChange = async (groupId: number, action: string) => {
    if (!hasGroupManageAccess(groupId)) {
      errorToast({
        message: 'You do not have permission to modify this group.',
      });
      return;
    }
    const newGroupId = action === 'add' ? groupId : 0;
    try {
      setSaveStatus('saving');
      await dispatch(setSelectedCrateGroupId(newGroupId));
      await dispatch(saveCrateChanges());
      setSaveStatus('saved');
    } catch (error) {
      console.error('Group change error:', error);
      setSaveStatus('error');
    }
  };

  const handleDeleteCrate = async () => {
    if (!crate?._id) return;

    try {
      const api = ApiService.getApiClient();
      await api.crateControllerDeleteCrate({ id: crate._id });
      successToast({ message: 'Crate deleted successfully' });
      history.push('/my-crates');
    } catch (error) {
      console.error('Delete crate error:', error);
      errorToast({ message: 'Failed to delete crate. Please try again.' });
    }
  };

  useEffect(() => {
    hasIncrementedView.current = false;
  }, [id]);

  // Fetches Crate from the API using its ID
  // Simplified: Just fetch and display - no preloading
  // File paths are loaded on-demand when Export modal opens
  // Track matches are loaded lazily when user expands a track row
  useEffect(() => {
    if (!id) return;

    if (shouldClearTrackMatchesForCrateId(id)) {
      dispatch(clearTrackMatches());
    }
    markTrackMatcherBoundToCrateId(id);

    dispatch(fetchCrateById(id))
      .then((response) => {
        if (fetchCrateById.fulfilled.match(response) && response.payload) {
          const crateWithConsistentIds = {
            ...response.payload,
            Tracks: response.payload.Tracks
              ? ensureTrackIds(response.payload.Tracks)
              : [],
          };

          dispatch(setSelectedCrate(crateWithConsistentIds));
          dispatch(setLocationFrom(locationFrom));
          const crateId = crateWithConsistentIds._id;
          if (crateId) {
            dispatch(
              seedCrateMetrics({
                crateId,
                viewCount: crateWithConsistentIds.viewCount ?? 0,
                saveCount: crateWithConsistentIds.saveCount ?? 0,
              }),
            );
          }
          if (!hasIncrementedView.current) {
            hasIncrementedView.current = true;
            incrementCrateView();
          }
        }
      })
      .catch((error) => {
        console.error('Failed to load crate:', error);
      });
  }, [id, dispatch]);

  // Fetch Serato play counts when component mounts or ID changes
  useEffect(() => {
    if (!isElectron()) return;
    const eventName = 'serato/get-all-play-counts';
    setSeratoPlayCountsLoading(true);
    const handlePlayCounts = (
      counts: Record<string, { playCount: number }>,
    ) => {
      setSeratoPlayCounts(counts || {});
      setSeratoPlayCountsLoading(false);
    };

    run(eventName);
    on(eventName, handlePlayCounts);

    // Cleanup listener on unmount or ID change
    return () => {
      removeListener(eventName);
    };
  }, [id]); // Re-fetch if crate ID changes

  useEffect(() => {
    if (!isElectron()) return;
    const eventName = 'virtualdj/get-all-play-counts';
    setVirtualDjPlayCountsLoading(true);
    const handlePlayCounts = (
      counts: Record<string, { playCount: number }>,
    ) => {
      setVirtualDjPlayCounts(counts || {});
      setVirtualDjPlayCountsLoading(false);
    };

    run(eventName);
    on(eventName, handlePlayCounts);

    return () => {
      removeListener(eventName);
    };
  }, [id]);

  // Load the user's groups with permissions
  useEffect(() => {
    const url = `${config.API_BASE_URL}/users/groups-with-permissions`;
    RequestService.fetch({ url })
      .then((resp: any[]) => {
        setGroupsWithPermissions(resp || []);
        setGroups(resp?.map((g: any) => ({ id: g.id, name: g.name })) || []);
      })
      .catch(() => {
        setGroups([]);
        setGroupsWithPermissions([]);
      });
  }, []);

  // Scroll detection for collapsed sticky header
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerBottom = headerRef.current.getBoundingClientRect().bottom;
        setIsHeaderCollapsed(headerBottom < 0);
      }
    };

    // Find the scrollable container - in Ionic/custom layouts, scrolling happens in a container div
    const scrollContainer =
      document.querySelector('.inner-scroll.scroll-y') ||
      document.querySelector('ion-content') ||
      window;

    if (scrollContainer && scrollContainer !== window) {
      (scrollContainer as HTMLElement).addEventListener(
        'scroll',
        handleScroll,
        {
          passive: true,
        },
      );
      return () =>
        (scrollContainer as HTMLElement).removeEventListener(
          'scroll',
          handleScroll,
        );
    } else {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const hasGroupManageAccess = useCallback(
    (groupId: number) =>
      groupsWithPermissions.some(
        (g: any) =>
          g.id === groupId &&
          (g.permission?.level === 'manager' ||
            g.permission?.level === 'owner'),
      ),
    [groupsWithPermissions],
  );

  // Load tracks and hackers from crate data in Redux state
  useEffect(() => {
    if (crate?.Hacker) {
      setHackers(crate.Hacker);
    }
  }, [crate]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const normalizeTrackFilePathMap = (
    trackInfo: iTrack[],
    filePathMap: Record<string, string[]>,
  ): Record<string, string[]> => {
    const normalized: Record<string, string[]> = {};
    // Track which filePathMap keys have already been claimed to prevent collisions
    const claimedSourceIds = new Set<string>();

    trackInfo.forEach((track) => {
      // Generate the canonical ID using artist+title hash (this should be unique per track)
      const canonicalId = sha256(
        JSON.stringify({
          artist: track.Artist?.trim() ?? '',
          title: track.Title?.trim() ?? '',
        }),
      ).toString();

      // List of possible IDs to check in the filePathMap, prioritizing canonical hash
      const possibleIds = [
        canonicalId,
        generateTrackID(track),
        track.ID,
        track._id,
        track.SpotifyID,
      ].filter((id): id is string => Boolean(id));

      let foundPaths: string[] | undefined;
      let matchedSourceId: string | undefined;

      // Find the first unclaimed ID that has paths
      for (const id of possibleIds) {
        if (
          filePathMap[id] &&
          filePathMap[id].length > 0 &&
          !claimedSourceIds.has(id)
        ) {
          foundPaths = filePathMap[id];
          matchedSourceId = id;
          break;
        }
      }

      if (foundPaths && foundPaths.length > 0 && matchedSourceId) {
        // Mark the source ID as claimed so no other track can use it
        claimedSourceIds.add(matchedSourceId);
        // Assign paths to all of this track's IDs
        possibleIds.forEach((id) => {
          normalized[id] = foundPaths!;
        });
      }
    });

    return normalized;
  };

  // Auto-save selected file paths to storage when they change
  useEffect(() => {
    if (!isElectron()) return;
    if (process.env.NODE_ENV !== 'production') {
      if (isInitialLoad) {
        setIsInitialLoad(false);
        return;
      }
    }
    // Skip auto-save during bulk loading operations
    if (bulkLoading) return;
    if (isElectron() && id !== undefined && crate?.Tracks) {
      const normalizedMap = normalizeTrackFilePathMap(
        crate.Tracks,
        selectedMatchesMap,
      );

      run('crates/save-track-file-paths', {
        crate_id: id,
        filePaths: normalizedMap,
      });
    }
  }, [selectedMatchesMap, bulkLoading, id, crate?.Tracks]);

  // Note: Bulk load for track matches is only needed for Select All/None/Unselect operations.
  // Export uses saved file paths from storage, which are fetched separately in Export.tsx.

  const getNormalizedSelectedMatches = useCallback(() => {
    if (!crate?.Tracks) return {};
    return normalizeTrackFilePathMap(crate.Tracks, selectedMatchesMap);
  }, [selectedMatchesMap, crate?.Tracks]);

  // Normalized per-source play count lookups (keyed by normalizePath +
  // lowercase, which is what mergePlayCounts produces). Passed down so each
  // matched local-file candidate can show its own Serato / VirtualDJ count.
  const playCountsBySource = useMemo(
    () => ({
      serato: mergePlayCounts(seratoPlayCounts),
      virtualdj: mergePlayCounts(virtualDjPlayCounts),
    }),
    [seratoPlayCounts, virtualDjPlayCounts],
  );

  const runSelectMostPlayed = useCallback(async () => {
    setIsLoading(true);
    setBulkLoading(true);
    try {
      await dispatch(
        bulkLoadAndSelect({
          crateId: id || '',
          tracks: filteredTracks,
          mode: 'mostPlayed',
          seratoPlayCounts: mergePlayCounts(
            seratoPlayCounts,
            virtualDjPlayCounts,
          ),
        }),
      ).unwrap();
    } finally {
      setBulkLoading(false);
      setIsLoading(false);
    }
  }, [dispatch, id, filteredTracks, seratoPlayCounts, virtualDjPlayCounts]);

  // If the user asked for "most played" while counts were still loading,
  // run it automatically once the play counts have finished loading.
  useEffect(() => {
    if (pendingMostPlayed && !playCountsLoading) {
      setPendingMostPlayed(false);
      runSelectMostPlayed();
    }
  }, [pendingMostPlayed, playCountsLoading, runSelectMostPlayed]);

  const handleDeleteTrack = async (track: iTrack) => {
    try {
      if (!track) {
        errorToast({
          message: `Unable to remove track: Track is undefined`,
        });
        return;
      }
      const possibleIds: string[] = [];
      if (track.ID) possibleIds.push(track.ID);
      if (track._id) possibleIds.push(track._id);
      if (track.SpotifyID) possibleIds.push(track.SpotifyID);
      const hashId = generateTrackID(track);
      if (hashId) possibleIds.push(hashId);

      const fallbackHash = sha256(
        JSON.stringify({
          artist: track.Artist?.trim() ?? '',
          title: track.Title?.trim() ?? '',
        }),
      ).toString();
      possibleIds.push(fallbackHash);

      if (possibleIds.length === 0) {
        errorToast({
          message: `Unable to remove track: No valid track data found`,
        });
        return;
      }

      possibleIds.forEach((id) => {
        dispatch(removeTrackFromState(id));
      });

      if (isElectron() && id) {
        const emptyFilePaths: Record<string, string[]> = {};
        possibleIds.forEach((trackId) => {
          emptyFilePaths[trackId] = [];
        });

        run('crates/save-track-file-paths', {
          crate_id: id,
          filePaths: emptyFilePaths,
        });
      }

      const trackIdForRemoval =
        track.ID || track._id || track.SpotifyID || hashId;

      if (!trackIdForRemoval) {
        errorToast({
          message: `Unable to remove track: Could not determine track ID`,
        });
        return;
      }
      await dispatch(removeTrackFromCrate(trackIdForRemoval));
      await dispatch(saveCrateChanges());

      successToast({
        message: `Removed ${track.Title || 'track'} from ${crate?.Title || 'crate'}`,
      });
    } catch (error) {
      errorToast({
        message: `Unable to remove ${track?.Title || 'track'}`,
      });
    }
  };

  const handleReorderTracks = async (newTrackOrder: iTrack[]) => {
    if (hidden?.length) {
      setTrackReorderInProgress(newTrackOrder);
      return;
    }
    await saveReorderedTracks(newTrackOrder);
  };

  const saveReorderedTracks = async (newTrackOrder: iTrack[]) => {
    const originalTrackOrder = filteredTracks;
    await dispatch(setCrateTracks(newTrackOrder));
    try {
      await dispatch(saveCrateChanges());
      setTrackReorderInProgress(null);
    } catch (error) {
      errorToast({
        message: `Unable to save changes`,
      });
      // Revert changes if unsuccessful
      await dispatch(setCrateTracks(originalTrackOrder));
    }
  };

  const handleAddTrackToCrate = async (trackId: string) => {
    const trackInCrate = crate?.Tracks?.find(
      (track) => track.ID === trackId,
      //(track) => track.SpotifyID === trackId,
    );

    // Avoids a user adding a track that is already in the current Crate
    if (trackInCrate) {
      errorToast({
        message: `${trackInCrate.Title} already exists in ${crate?.Title}`,
      });
      return;
    }
    try {
      const track = await dispatch(fetchTrackById(trackId));
      if (fetchTrackById.fulfilled.match(track) && track.payload) {
        await dispatch(addTrackToCrate(track.payload));
        await dispatch(saveCrateChanges());
      }
    } catch (error) {
      console.error('Error adding track:', error); // Handle any potential errors
      errorToast({ message: 'Unable to add track' });
    }
  };

  // Saves a crate to a user's "My Crates"
  const saveToMyCrates = useCallback(async () => {
    setIsLoading(true);
    if (crate) {
      if (crate._id && locationFrom !== 'charts') {
        dispatch(incrementCrateMetric({ crateId: crate._id, metric: 'save' }));
      }
      CrateService.createMyCrate({
        ...crate,
      })
        .then((response) => {
          // PostHog Save Crate Event
          try {
            if (PostHogService.isInitialized()) {
              PostHogService.capture('curated_crate_saved', {
                crate_id: response.id,
                crate_title: crate.Title,
                original_crate_id: crate._id,
                crate_type: crate.Type || 'unknown',
                track_count: crate.Tracks?.length || 0,
                has_image: !!crate.Image,
                group_id: 0,
                is_electron: isElectron(),
                app_version: config.VERSION,
              });
            }
          } catch (error) {
            console.error('Error capturing PostHog save Crate event:', error);
          }

          run('crates/save-track-file-paths', {
            crate_id: response.id,
            filePaths: selectedMatchesMap,
          });
          history.push('/my-crates');
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Failed to save crate:', error);
          try {
            if (PostHogService.isInitialized()) {
              PostHogService.capture('curated_crate_save_failed', {
                crate_title: crate.Title,
                original_crate_id: crate._id,
                crate_type: crate.Type || 'unknown',
                track_count: crate.Tracks?.length || 0,
                error_type: error?.message ? 'api_error' : 'unknown_error',
                is_electron: isElectron(),
                app_version: config.VERSION,
              });
            }
          } catch (err) {
            console.error('Error capturing PostHog save Crate event:', err);
          }

          errorToast({ message: 'Failed to save crate. Please try again.' });
          setIsLoading(false);
        });
    }
  }, [crate, history, setIsLoading, selectedMatchesMap]);

  // Saves a crate to user's "My Crates" and then adds it to specified group
  const saveToMyCratesAndAddToGroup = useCallback(
    (groupId: number, groupName: string) => {
      setIsLoading(true);
      if (crate) {
        // Step 1: Create the crate (will be created with groupId = 0)
        CrateService.createMyCrate({
          ...crate,
        })
          .then((response) => {
            // Step 2: Save track file paths
            run('crates/save-track-file-paths', {
              crate_id: response.id,
              filePaths: selectedMatchesMap,
            });

            // Step 3: Update the crate to assign it to the group
            CrateService.updateMyCrate({
              ...response,
              groupId: groupId,
            })
              .then(() => {
                // PostHog save + group assignment
                try {
                  if (PostHogService.isInitialized()) {
                    PostHogService.capture('curated_crate_saved_to_group', {
                      crate_id: response.id,
                      crate_title: crate.Title,
                      original_crate_id: crate._id,
                      crate_type: crate.Type || 'unknown',
                      track_count: crate.Tracks?.length || 0,
                      has_image: !!crate.Image,
                      group_id: groupId,
                      group_name: groupName,
                      is_electron: isElectron(),
                      app_version: config.VERSION,
                    });
                  }
                } catch (error) {
                  console.error(
                    'Error capturing PostHog save Crate to group event:',
                    error,
                  );
                }
                successToast({
                  message: `Saved to My Crates and added to ${groupName}`,
                });
                history.push('/my-crates');
                setIsLoading(false);
              })
              .catch((error: any) => {
                console.error('Error adding crate to group:', error);
                try {
                  if (PostHogService.isInitialized()) {
                    PostHogService.capture('curated_crate_saved_group_failed', {
                      crate_id: response.id,
                      crate_title: crate.Title,
                      original_crate_id: crate._id,
                      crate_type: crate.Type || 'unknown',
                      track_count: crate.Tracks?.length || 0,
                      group_id: groupId,
                      group_name: groupName,
                      error_type: 'group_assignment_failed',
                      is_electron: isElectron(),
                      app_version: config.VERSION,
                    });
                  }
                } catch (err) {
                  console.error(
                    'Error capturing PostHog save Crate to group event:',
                    err,
                  );
                }
                errorToast({
                  message: `Saved to My Crates but failed to add to ${groupName}`,
                });
                history.push('/my-crates');
                setIsLoading(false);
              });
          })
          .catch((error: any) => {
            console.error('Error saving to my crates:', error);
            try {
              if (PostHogService.isInitialized()) {
                PostHogService.capture('curated_crate_save_failed', {
                  crate_title: crate.Title,
                  original_crate_id: crate._id,
                  crate_type: crate.Type || 'unknown',
                  track_count: crate.Tracks?.length || 0,
                  intended_group_id: groupId,
                  intended_group_name: groupName,
                  error_type: error?.message ? 'api_error' : 'unknown_error',
                  is_electron: isElectron(),
                  app_version: config.VERSION,
                });
              }
            } catch (err) {
              console.error('Error capturing PostHog save Crate event:', err);
            }

            errorToast({
              message: `Failed to save crate`,
            });
            setIsLoading(false);
          });
      }
    },
    [
      crate,
      history,
      setIsLoading,
      selectedMatchesMap,
      successToast,
      errorToast,
    ],
  );

  // Scrolls the hidden tracks list into view
  const scrollFilteredTracksIntoView = () => {
    if (document.activeElement?.tagName === 'ION-INPUT') {
      return;
    }
    hiddenTracksRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const currentGroup = groups.find((g) => g.id === crate?.groupId);
  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      Published: '#2dd36f',
      'Pending Review': '#ffc409',
      Draft: '#92949c',
      Archived: '#5a5f6f',
    };
    return colors[status] || '#92949c';
  };

  return (
    <>
      {/* Saving spinner appears any time we're persisting track changes to the API */}
      <SavingSpinner isOpen={isSaving} />

      {/* Unified Save Status Indicator */}
      <SaveStatusIndicator
        status={saveStatus}
        onRetry={() => handleAutoSave()}
      />

      {/* Collapsed Sticky Header - Shows when scrolled past main header */}
      <div
        className={`crate-sticky-header ${isHeaderCollapsed ? 'visible' : ''}`}
        style={
          extractedColor
            ? {
                background: `linear-gradient(90deg, ${extractedColor}ee 0%, ${extractedColor}88 50%, rgba(12, 12, 12, 0.98) 100%)`,
              }
            : undefined
        }
      >
        <div className="sticky-header-content">
          {/* Thumbnail */}
          <div className="sticky-header-thumbnail">
            {crate?.Image ? (
              <img src={ensureAbsoluteUrl(crate.Image)} alt={crate?.Title} />
            ) : (
              <IonImg src={icons.flameLogo} alt="" />
            )}
          </div>

          {/* Title & Info */}
          <div className="sticky-header-info">
            <h2 className="sticky-header-title">{crate?.Title}</h2>
            <span className="sticky-header-meta">
              {hackers?.[0]?.name}
              {currentGroup && ` • ${currentGroup.name}`}
              {filteredTracks.length > 0 &&
                ` • ${filteredTracks.length} tracks`}
            </span>
          </div>

          {/* Actions */}
          <div className="sticky-header-actions">
            {/* Cloud Crates / Streaming Buttons */}
            {crate && filteredTracks.length > 0 && (
              <StreamingSaveButtons
                crateData={crate}
                tracks={filteredTracks}
                compact
              />
            )}

            {/* Save to My Crates */}
            {crate && crate.Type !== 'User' && (
              <IonButton
                fill="clear"
                className="sticky-action-btn"
                onClick={saveToMyCrates}
                title="Save to My Crates"
              >
                <IonIcon slot="icon-only" icon={heartOutline} />
              </IonButton>
            )}

            {/* Inline Export Buttons - Sticky */}
            <div className="sticky-export-btn">
              <InlineExportButtons
                crateId={crate?._id}
                crateName={crate?.Title || ''}
                tracks={selectedMatchesMap}
                trackInfo={filteredTracks}
                hacker={hackers?.map((h) => h.name).join(', ') || ''}
                cloudCrateList={generateCloudCrateMap(crate)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modern Card-Based Header */}
      <div ref={topRef}>
        <div ref={headerRef}>
          <IonCard
            data-id={crate?._id}
            className={
              `crate-header-card` + (isFilterActive ? ' filter-active' : '')
            }
            style={
              extractedColor
                ? {
                    background: `linear-gradient(140deg, ${extractedColor} 0%, ${extractedColor}88 40%, ${extractedColor}33 78%, rgba(12, 12, 12, 0.98) 100%)`,
                  }
                : undefined
            }
          >
            <IonCardContent className="crate-header-content">
              <div className="crate-header-layout">
                {/* Left: Image */}
                <div className="crate-header-image-section">
                  <ImageCropper
                    editable={isEditable}
                    onImageChange={handleImageChange}
                    crateTitle={crate?.Title}
                    currentImageUrl={
                      crate?.Image ? ensureAbsoluteUrl(crate.Image) : undefined
                    }
                    onColorExtracted={setExtractedColor}
                  />
                  {!crate?.Image && !isEditable && (
                    <IonImg
                      alt={crate?.Title}
                      src={icons.flameLogo}
                      className="crate-header-fallback-image"
                    />
                  )}
                </div>

                {/* Center: Main Content */}
                <div className="crate-header-main-content">
                  {/* Top Meta Row - Spotify Style */}
                  <div className="crate-header-meta-row">
                    <div className="crate-header-badges">
                      {/* Crate Type Badge/Button */}
                      {(() => {
                        const availableGroups = groups.filter(
                          (g) =>
                            g.id !== 0 &&
                            g.id !== crate?.groupId &&
                            hasGroupManageAccess(g.id),
                        );
                        // Business plan teams (excluding Crate Hackers group and current group)
                        const businessTeams = groups.filter(
                          (g) =>
                            g.id !== 0 &&
                            g.id !== 1 &&
                            g.id !== crate?.groupId &&
                            hasGroupManageAccess(g.id),
                        );
                        const hasActions =
                          (currentGroup &&
                            isEditable &&
                            crate?.groupId === 1) ||
                          (!currentGroup && availableGroups.length > 0) ||
                          // Business plan users can add any crate to their teams
                          (hasBusinessPlan && businessTeams.length > 0);

                        return hasActions ? (
                          <button
                            id="crate-type-trigger"
                            className="crate-type-spotify-btn"
                            onClick={() => setShowActionsMenu(true)}
                          >
                            {currentGroup ? currentGroup.name : 'My Crate'}
                            {crate?.groupId === 1 && (
                              <span
                                className="status-badge-inline"
                                style={{
                                  backgroundColor: getStatusColor(
                                    crate?.Status || 'Draft',
                                  ),
                                }}
                              >
                                {crate?.Status || 'Draft'}
                              </span>
                            )}
                            <IonIcon icon={chevronDown} />
                          </button>
                        ) : (
                          <span className="crate-type-spotify-text">
                            {currentGroup ? currentGroup.name : 'My Crate'}
                            {crate?.groupId === 1 && (
                              <span
                                className="status-badge-inline"
                                style={{
                                  backgroundColor: getStatusColor(
                                    crate?.Status || 'Draft',
                                  ),
                                }}
                              >
                                {crate?.Status || 'Draft'}
                              </span>
                            )}
                          </span>
                        );
                      })()}

                      {/* Unified Actions Popover */}
                      <IonPopover
                        trigger="crate-type-trigger"
                        isOpen={showActionsMenu}
                        onDidDismiss={() => setShowActionsMenu(false)}
                        className="crate-unified-actions-popover"
                      >
                        <IonList lines="none">
                          {/* Curator Actions Section (for Crate Hackers group crates) */}
                          {currentGroup &&
                            isEditable &&
                            crate?.groupId === 1 && (
                              <>
                                <IonItem className="menu-section-header">
                                  <IonIcon icon={settings} slot="start" />
                                  <IonText>
                                    <strong>Curator Actions</strong>
                                  </IonText>
                                </IonItem>
                                <IonItem
                                  button
                                  className="menu-sub-item"
                                  onClick={async () => {
                                    setSaveStatus('saving');
                                    await dispatch(
                                      setSelectedCrateStatus('Draft'),
                                    );
                                    await dispatch(saveCrateChanges());
                                    setSaveStatus('saved');
                                    setShowActionsMenu(false);
                                  }}
                                >
                                  <span
                                    className="status-dot"
                                    style={{
                                      backgroundColor: getStatusColor('Draft'),
                                    }}
                                  />
                                  Set to Draft
                                </IonItem>
                                <IonItem
                                  button
                                  className="menu-sub-item"
                                  onClick={async () => {
                                    setSaveStatus('saving');
                                    await dispatch(
                                      setSelectedCrateStatus('Pending Review'),
                                    );
                                    await dispatch(saveCrateChanges());
                                    setSaveStatus('saved');
                                    setShowActionsMenu(false);
                                  }}
                                >
                                  <span
                                    className="status-dot"
                                    style={{
                                      backgroundColor:
                                        getStatusColor('Pending Review'),
                                    }}
                                  />
                                  Set to Pending Review
                                </IonItem>
                                <IonItem
                                  button
                                  className="menu-sub-item"
                                  onClick={async () => {
                                    setSaveStatus('saving');
                                    await dispatch(
                                      setSelectedCrateStatus('Published'),
                                    );
                                    await dispatch(saveCrateChanges());
                                    setSaveStatus('saved');
                                    setShowActionsMenu(false);
                                  }}
                                >
                                  <span
                                    className="status-dot"
                                    style={{
                                      backgroundColor:
                                        getStatusColor('Published'),
                                    }}
                                  />
                                  Set to Published
                                </IonItem>
                                <IonItem
                                  button
                                  className="menu-sub-item"
                                  onClick={async () => {
                                    setSaveStatus('saving');
                                    await dispatch(
                                      setSelectedCrateStatus('Archived'),
                                    );
                                    await dispatch(saveCrateChanges());
                                    setSaveStatus('saved');
                                    setShowActionsMenu(false);
                                  }}
                                >
                                  <span
                                    className="status-dot"
                                    style={{
                                      backgroundColor:
                                        getStatusColor('Archived'),
                                    }}
                                  />
                                  Set to Archived
                                </IonItem>
                                <div className="menu-divider" />
                                <IonItem
                                  button
                                  className="menu-sub-item"
                                  id="edit-categories-trigger"
                                  onClick={() => {
                                    setShowActionsMenu(false);
                                    // Trigger category select to open
                                    const categorySelect =
                                      document.querySelector(
                                        '.crate-header-categories ion-select',
                                      ) as HTMLElement;
                                    categorySelect?.click();
                                  }}
                                >
                                  <IonIcon icon={pricetag} slot="start" />
                                  Edit Categories
                                </IonItem>
                                <IonItem
                                  button
                                  className="menu-sub-item"
                                  onClick={() => {
                                    setShowActionsMenu(false);
                                    setShowStreamingLinksModal(true);
                                  }}
                                >
                                  <IonIcon
                                    icon={icons.cloudCrates}
                                    slot="start"
                                  />
                                  Edit Streaming Links
                                </IonItem>
                                <IonItem
                                  button
                                  className="menu-sub-item"
                                  onClick={async () => {
                                    if (!crate?.Tracks?.length) {
                                      setShowActionsMenu(false);
                                      return;
                                    }

                                    setSaveStatus('saving');

                                    const cleanedTracks = crate.Tracks.map(
                                      (track) => ({
                                        ...track,
                                        Title: cleanTitle(track.Title),
                                        Artist: cleanArtist(track.Artist),
                                      }),
                                    );

                                    const modifiedCount = crate.Tracks.filter(
                                      (track, i) =>
                                        track.Title !==
                                          cleanedTracks[i].Title ||
                                        track.Artist !==
                                          cleanedTracks[i].Artist,
                                    ).length;

                                    if (modifiedCount === 0) {
                                      setSaveStatus('saved');
                                      setShowActionsMenu(false);
                                      return;
                                    }

                                    // Store original tracks for undo
                                    setPreCleanupTracks([...crate.Tracks]);

                                    dispatch(setCrateTracks(cleanedTracks));
                                    await dispatch(saveCrateChangesNow());
                                    setSaveStatus('saved');
                                    setShowActionsMenu(false);
                                  }}
                                >
                                  <IonIcon icon={colorWand} slot="start" />
                                  Clean up Titles & Artists
                                </IonItem>
                                {preCleanupTracks && (
                                  <IonItem
                                    button
                                    className="menu-sub-item"
                                    onClick={async () => {
                                      setSaveStatus('saving');
                                      dispatch(
                                        setCrateTracks(preCleanupTracks),
                                      );
                                      await dispatch(saveCrateChangesNow());
                                      setPreCleanupTracks(null);
                                      setSaveStatus('saved');
                                      setShowActionsMenu(false);
                                    }}
                                  >
                                    <IonIcon icon={arrowBack} slot="start" />
                                    Undo Title & Artist Clean up
                                  </IonItem>
                                )}
                                <IonItem
                                  button
                                  className="menu-sub-item danger-item"
                                  onClick={() => {
                                    setShowActionsMenu(false);
                                    setShowDeleteConfirm(true);
                                  }}
                                >
                                  <IonIcon icon={trash} slot="start" />
                                  Delete Crate
                                </IonItem>
                              </>
                            )}

                          {/* Curator Section - Add to Crate Hackers */}
                          {(() => {
                            const crateHackersGroup = groups.find(
                              (g) => g.id === 1,
                            );
                            const canAddToCrateHackers =
                              crateHackersGroup &&
                              crate?.groupId !== 1 &&
                              hasGroupManageAccess(1);

                            if (!canAddToCrateHackers) return null;

                            return (
                              <>
                                {currentGroup && (
                                  <div className="menu-divider" />
                                )}
                                <IonItem className="menu-section-header">
                                  <IonIcon icon={settings} slot="start" />
                                  <IonText>
                                    <strong>Curator</strong>
                                  </IonText>
                                </IonItem>
                                <IonItem
                                  button
                                  className="menu-sub-item"
                                  onClick={() => {
                                    handleGroupIdChange(1, 'add');
                                    setShowActionsMenu(false);
                                  }}
                                >
                                  <IonIcon icon={add} slot="start" />
                                  Add as Curated Crate
                                </IonItem>
                              </>
                            );
                          })()}

                          {/* Business Plan Teams Section */}
                          {(() => {
                            const businessTeams = groups
                              .filter(
                                (group) =>
                                  group.id !== 0 &&
                                  group.id !== 1 &&
                                  group.id !== crate?.groupId &&
                                  hasGroupManageAccess(group.id),
                              )
                              .sort((a, b) => a.name.localeCompare(b.name));

                            if (businessTeams.length === 0) return null;

                            return (
                              <>
                                <div className="menu-divider" />
                                <IonItem className="menu-section-header">
                                  <IonIcon icon={briefcase} slot="start" />
                                  <IonText>
                                    <strong>Business Plan Teams</strong>
                                  </IonText>
                                </IonItem>
                                {businessTeams.map((group) => (
                                  <IonItem
                                    key={group.id}
                                    button
                                    className="menu-sub-item"
                                    onClick={() => {
                                      handleGroupIdChange(group.id, 'add');
                                      setShowActionsMenu(false);
                                    }}
                                  >
                                    <IonIcon icon={add} slot="start" />
                                    {group.name}
                                  </IonItem>
                                ))}
                              </>
                            );
                          })()}
                        </IonList>
                      </IonPopover>
                    </div>
                  </div>

                  {/* Title and Metrics Section */}
                  <div className="crate-title-section">
                    <h1
                      ref={titleRef}
                      className={`crate-title-display ${isEditable ? 'editable' : ''}`}
                      contentEditable={isEditable}
                      suppressContentEditableWarning
                      onBlur={(e) => {
                        const newTitle = e.currentTarget.textContent || '';
                        if (newTitle !== crate?.Title) {
                          handleTitleChange({ target: { value: newTitle } });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                    >
                      {crate?.Title}
                    </h1>
                    <CrateMetrics
                      viewCount={crate?.viewCount || 0}
                      saveCount={crate?.saveCount || 0}
                      size="medium"
                    />
                  </div>

                  {/* Hackers & Group */}
                  <div className="crate-header-byline">
                    <HackersInline hackers={hackers} />
                    {currentGroup && (
                      <>
                        <span className="group-separator">, </span>
                        <span className="group-name">{currentGroup.name}</span>
                      </>
                    )}
                  </div>

                  {/* Description */}
                  <p
                    className={`crate-description-text ${isEditable ? 'editable' : ''} ${isGeneratingDescription ? 'generating' : ''}`}
                    contentEditable={isEditable && !isGeneratingDescription}
                    suppressContentEditableWarning
                    onBlur={(e) => {
                      const newDescription = e.currentTarget.textContent || '';
                      if (newDescription !== crate?.Description) {
                        handleDescriptionChange(newDescription);
                      }
                    }}
                  >
                    {isGeneratingDescription
                      ? 'Generating description...'
                      : crate?.Description || 'No description'}
                  </p>

                  {/* Video link in header */}
                  {isEditable && (
                    <span
                      className="crate-video-link"
                      onClick={() => setEditingVideo(true)}
                    >
                      {crate?.videoEmbedCode ? 'Edit Video' : '+ Add Video'}
                    </span>
                  )}
                </div>
              </div>

              {/* Header Footer Actions - Full width row below the layout */}
              <div className="header-footer-actions">
                {/* Save to My Crates - Left side */}
                {crate && crate.Type !== 'User' && (
                  <div className="export-group save-group">
                    <span className="group-label">Save to My Crates</span>
                    <IonButton
                      className="export-btn save-btn"
                      fill="clear"
                      size="small"
                      onClick={saveToMyCrates}
                      title="Save to My Crates"
                    >
                      <IonIcon icon={saveOutline} />
                    </IonButton>
                  </div>
                )}

                {/* Streaming Export Buttons */}
                {crate && filteredTracks.length > 0 && (
                  <StreamingSaveButtons
                    crateData={crate}
                    tracks={filteredTracks}
                  />
                )}

                <div className="export-options-container">
                  <span className="panel-title">Export Options</span>

                  {/* DJ Software + Other Export Buttons */}
                  <InlineExportButtons
                    crateId={crate?._id}
                    crateName={crate?.Title || ''}
                    tracks={selectedMatchesMap}
                    trackInfo={filteredTracks}
                    hacker={hackers?.map((h) => h.name).join(', ') || ''}
                    cloudCrateList={generateCloudCrateMap(crate)}
                  />
                </div>
              </div>

              {/* Hidden Components - Accessed via dropdown */}
              <div
                style={{
                  position: 'absolute',
                  visibility: 'hidden',
                  pointerEvents: 'none',
                }}
              >
                <div className="crate-header-categories">
                  <CategoriesEditable />
                </div>
              </div>
            </IonCardContent>
          </IonCard>
        </div>
      </div>
      {/* Progress Bar for Bulk Operations */}
      {isElectron() && bulkProgress.active && (
        <div className="progress-bar-compact">
          <IonText className="progress-text">
            Processing {bulkProgress.completed}/{bulkProgress.total}
          </IonText>
          <IonProgressBar
            value={bulkProgress.completed / bulkProgress.total}
            color="primary"
          />
        </div>
      )}

      {/* Video Embed Display */}
      {crate?.videoEmbedCode && !editingVideo && (
        <div className="crate-video-section">
          {isElectron() ? (
            <div
              className="crate-video-thumbnail"
              onClick={() =>
                openExternalLink(getVideoUrlFromEmbed(crate.videoEmbedCode!))
              }
            >
              <img
                src={getThumbnailFromEmbed(crate.videoEmbedCode)}
                alt="Video thumbnail"
              />
              <div className="crate-video-play-btn">
                <span>&#9654;</span>
              </div>
            </div>
          ) : (
            <div
              className="crate-video-player"
              dangerouslySetInnerHTML={{
                __html: crate.videoEmbedCode,
              }}
            />
          )}
        </div>
      )}

      {/* Video Embed Editor */}
      {editingVideo && (
        <div className="crate-video-editor">
          <IonLabel style={{ fontWeight: 600, fontSize: '14px' }}>
            Video Embed Code
          </IonLabel>
          <IonTextarea
            fill="outline"
            placeholder='Paste iframe embed code (e.g. <iframe src="https://www.youtube.com/embed/..."></iframe>)'
            value={crate?.videoEmbedCode || ''}
            onIonChange={(e) => handleVideoChange(e.target.value!)}
            style={{
              minHeight: '100px',
              backgroundColor: 'rgba(255,255,255,0.06)',
              borderRadius: '8px',
              marginTop: '8px',
              fontSize: '13px',
            }}
          />
          <IonText color="medium" style={{ marginTop: '4px' }}>
            <small>
              Supports YouTube, Vimeo, Dailymotion &amp; Kartra embed codes.
            </small>
          </IonText>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <IonButton
              fill="solid"
              color="primary"
              size="small"
              onClick={handleSaveVideoChange}
            >
              Save
            </IonButton>
            <IonButton
              fill="outline"
              color="medium"
              size="small"
              onClick={() => setEditingVideo(false)}
            >
              Cancel
            </IonButton>
          </div>
        </div>
      )}

      {/* Crate Filter */}
      {isEditable && (
        <CrateFilter
          onShowFilteredTracks={scrollFilteredTracksIntoView}
          onMagicSort={handleReorderTracks}
        />
      )}
      {withTracks && (
        <div className="crate-tracks-container">
          {/* Track Matcher Options - Directly above tracklist */}
          {isElectron() && (
            <div className="track-matcher-options">
              <span className="matcher-label">Track Matcher Options</span>
              <div className="matcher-toggle-wrapper">
                <span
                  className={`toggle-state-label ${!showTrackMatcher ? 'active' : ''}`}
                >
                  Off
                </span>
                <IonToggle
                  onIonChange={(e) =>
                    dispatch(setShowMatcher(e.detail.checked))
                  }
                  checked={showTrackMatcher}
                  className="matcher-toggle"
                />
                <span
                  className={`toggle-state-label ${showTrackMatcher ? 'active' : ''}`}
                >
                  On
                </span>
              </div>
              {showTrackMatcher && (
                <>
                  <div className="matcher-buttons">
                    <button
                      className="matcher-btn"
                      onClick={async () => {
                        try {
                          if (PostHogService.isInitialized()) {
                            PostHogService.capture(
                              'track_matcher_select_first_clicked',
                              {
                                crate_id: id,
                                crate_title: crate?.Title,
                                track_count: filteredTracks.length,
                                is_electron: isElectron(),
                                app_version: config.VERSION,
                              },
                            );
                          }
                        } catch (error) {
                          console.error(
                            'Error capturing PostHog select first click:',
                            error,
                          );
                        }

                        setIsLoading(true);
                        setBulkLoading(true);
                        await dispatch(
                          bulkLoadAndSelect({
                            crateId: id || '',
                            tracks: filteredTracks,
                            mode: 'first',
                          }),
                        ).unwrap();
                        setBulkLoading(false);
                        setIsLoading(false);
                      }}
                    >
                      Select First
                    </button>
                    <button
                      className="matcher-btn"
                      onClick={async () => {
                        try {
                          if (PostHogService.isInitialized()) {
                            PostHogService.capture(
                              'track_matcher_select_most_played_clicked',
                              {
                                crate_id: id,
                                crate_title: crate?.Title,
                                track_count: filteredTracks.length,
                                is_electron: isElectron(),
                                app_version: config.VERSION,
                              },
                            );
                          }
                        } catch (error) {
                          console.error(
                            'Error capturing PostHog select most played click:',
                            error,
                          );
                        }

                        // Play counts load in the background. If they're not
                        // ready yet, defer and show a loading note instead of
                        // acting on incomplete data.
                        if (playCountsLoading) {
                          setPendingMostPlayed(true);
                          return;
                        }

                        await runSelectMostPlayed();
                      }}
                    >
                      Select Most Played
                    </button>
                    <button
                      className="matcher-btn"
                      onClick={async () => {
                        try {
                          if (PostHogService.isInitialized()) {
                            PostHogService.capture(
                              'track_matcher_select_all_clicked',
                              {
                                crate_id: id,
                                crate_title: crate?.Title,
                                track_count: filteredTracks.length,
                                is_electron: isElectron(),
                                app_version: config.VERSION,
                              },
                            );
                          }
                        } catch (error) {
                          console.error(
                            'Error capturing PostHog select all click:',
                            error,
                          );
                        }
                        setIsLoading(true);
                        setBulkLoading(true);
                        await dispatch(
                          bulkLoadAndSelect({
                            crateId: id || '',
                            tracks: filteredTracks,
                            mode: 'all',
                          }),
                        ).unwrap();
                        setBulkLoading(false);
                        setIsLoading(false);
                      }}
                    >
                      Select All
                    </button>
                    <button
                      className="matcher-btn"
                      onClick={async () => {
                        try {
                          if (PostHogService.isInitialized()) {
                            PostHogService.capture(
                              'track_matcher_unselect_all_clicked',
                              {
                                crate_id: id,
                                crate_title: crate?.Title,
                                track_count: filteredTracks.length,
                                is_electron: isElectron(),
                                app_version: config.VERSION,
                              },
                            );
                          }
                        } catch (error) {
                          console.error(
                            'Error capturing PostHog unselect all click:',
                            error,
                          );
                        }
                        setIsLoading(true);
                        setBulkLoading(true);
                        await dispatch(
                          bulkLoadAndSelect({
                            crateId: id || '',
                            tracks: filteredTracks,
                            mode: 'none',
                          }),
                        ).unwrap();
                        setBulkLoading(false);
                        setIsLoading(false);
                      }}
                    >
                      Unselect All
                    </button>
                  </div>

                  {pendingMostPlayed && playCountsLoading && (
                    <div className="matcher-loading-note">
                      <IonSpinner name="crescent" />
                      <span>
                        Loading play counts… Most played will run automatically
                        when ready.
                      </span>
                    </div>
                  )}

                  <div className="match-filter-container">
                    <div className="match-stats">
                      <span className="stat-item">
                        <strong>{matchedCount}</strong> /{' '}
                        {crate?.Tracks?.length || 0} matched
                      </span>
                      <span className="stat-separator">•</span>
                      <span className="stat-item">
                        <strong>{reviewCount}</strong> review
                      </span>
                    </div>

                    <div className="match-filter-segmented">
                      <button
                        className={`filter-btn ${matchFilter === 'all' ? 'active' : ''}`}
                        onClick={() => dispatch(setMatchFilter('all'))}
                      >
                        All
                      </button>
                      <button
                        className={`filter-btn ${matchFilter === 'matched' ? 'active matched-active' : ''}`}
                        onClick={() => dispatch(setMatchFilter('matched'))}
                      >
                        Matched
                      </button>
                      <button
                        className={`filter-btn ${matchFilter === 'unmatched' ? 'active unmatched-active' : ''}`}
                        onClick={() => dispatch(setMatchFilter('unmatched'))}
                      >
                        Unmatched
                      </button>
                      <button
                        className={`filter-btn ${matchFilter === 'review' ? 'active review-active' : ''}`}
                        onClick={() => dispatch(setMatchFilter('review'))}
                      >
                        Review
                      </button>
                    </div>
                  </div>

                  <div className="matcher-bulk-actions"></div>
                </>
              )}
            </div>
          )}

          <Tracks
            editable={isEditable}
            showTrackMatcher={showTrackMatcher}
            tracks={filteredTracks}
            playCountsBySource={playCountsBySource}
            onDeleteTrack={handleDeleteTrack}
            onReorderTracks={handleReorderTracks}
          />
          <HiddenTracks
            ref={hiddenTracksRef}
            tracks={hidden}
            onDeleteTrack={handleDeleteTrack}
          />
        </div>
      )}

      {isEditable && (
        <>
          <h2>Search</h2>
          <SearchComponent handleClick={handleAddTrackToCrate} />
        </>
      )}

      {/* Scroll to Top Button */}
      <div className="scroll-to-top-container">
        <IonButton
          fill="clear"
          className="scroll-to-top-btn"
          onClick={() => {
            topRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <IonIcon icon={arrowUp} slot="icon-only" />
        </IonButton>
      </div>

      {filteredTracks?.length > 0 && filteredTracks?.at(-1) && isEditable && (
        <Recommendations
          ID={filteredTracks?.at(-1)?.SpotifyID || ''}
          Artist={filteredTracks?.at(-1)?.Artist || ''}
          Title={filteredTracks?.at(-1)?.Title || ''}
          Key_Camelot={filteredTracks?.at(-1)?.Key_Camelot || undefined}
          keyMatches={filteredTracks?.at(-1)?.keyMatches || undefined}
          showAddTrack={isEditable}
          addTrackHandler={(trackId) => {
            handleAddTrackToCrate(trackId);
          }}
        />
      )}
      <CrateFilterReorderDialog
        isOpen={!!trackReorderInProgress}
        onDismiss={() => setTrackReorderInProgress(null)}
        numHiddenTracks={hidden?.length ?? 0}
        onConfirm={() => {
          if (trackReorderInProgress) {
            saveReorderedTracks(trackReorderInProgress.concat(hidden));
          }
          setTrackReorderInProgress(null);
        }}
      />

      {/* Delete Crate Confirmation */}
      <IonAlert
        isOpen={showDeleteConfirm}
        onDidDismiss={() => setShowDeleteConfirm(false)}
        header="Delete Crate"
        message={`Are you sure you want to permanently delete "${crate?.Title}"? This action cannot be undone.`}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Delete',
            role: 'destructive',
            handler: handleDeleteCrate,
          },
        ]}
      />
      <IonAlert
        isOpen={showRegenerateConfirm}
        onDidDismiss={() => {
          setShowRegenerateConfirm(false);
          setPendingTitleChange(null);
        }}
        header="Regenerate Description?"
        message="You've manually edited the description. Do you want to regenerate it based on the new title? This will overwrite your changes."
        buttons={[
          {
            text: 'Keep Current',
            role: 'cancel',
            handler: () => {
              setPendingTitleChange(null);
              handleAutoSave();
            },
          },
          {
            text: 'Regenerate',
            role: 'confirm',
            handler: confirmRegenerateDescription,
          },
        ]}
      />

      {/* Edit Streaming Links Modal */}
      {crate && (
        <CloudCrates
          editable={false}
          crateData={crate}
          onUpdateStreamingLinks={handleStreamingLinkUpdate}
          externalModalOpen={showStreamingLinksModal}
          onExternalModalClose={() => setShowStreamingLinksModal(false)}
        />
      )}
    </>
  );
};

export const generateCloudCrateMap = (crate: iCrate | null) => {
  const cloudCrateMap: ICloudCrateLink[] = [];

  if (crate?.StreamDeezer) {
    cloudCrateMap.push({
      name: 'deezer',
      link: crate.StreamDeezer,
    });
  }

  if (crate?.StreamTidal) {
    cloudCrateMap.push({
      name: 'tidal',
      link: crate.StreamTidal,
    });
  }

  if (crate?.StreamSoundcloud) {
    cloudCrateMap.push({
      name: 'soundcloud',
      link: crate.StreamSoundcloud,
    });
  }

  if (crate?.StreamAppleMusic) {
    cloudCrateMap.push({
      name: 'apple',
      link: crate.StreamAppleMusic,
    });
  }

  if (crate?.StreamBeatsource) {
    cloudCrateMap.push({
      name: 'beatport',
      link: crate.StreamBeatsource.replace(/beatsource\.com/i, 'beatport.com'),
    });
  }

  if (crate?.StreamSpotify) {
    cloudCrateMap.push({
      name: 'spotify',
      link: crate.StreamSpotify,
    });
  }
  return cloudCrateMap;
};
